/**
 * Mirrors PayrollEngineService frequency scaling for salary-entry previews.
 * Stored salary figures follow the employee's salary basis; payroll runs scale
 * them to each pay period while statutory taxes use a monthly equivalent.
 */

import { PAYROLL_FREQUENCIES } from "@/utils/payrollPeriodHelpers";

export { PAYROLL_FREQUENCIES };

export function normalizePayFrequency(value) {
  const frequency = String(value || "monthly")
    .trim()
    .toLowerCase();
  return PAYROLL_FREQUENCIES.includes(frequency) ? frequency : "monthly";
}

export function normalizeSalaryBasis(value) {
  const basis = String(value || "monthly")
    .trim()
    .toLowerCase();
  return basis || "monthly";
}

export function capitalizeToken(value) {
  const str = String(value || "").trim();
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getMonthlyEquivalentFactor(frequency, config = {}) {
  const normalized = normalizePayFrequency(frequency);
  if (normalized === "monthly") return 1;

  const attendanceDays = Math.max(
    Number(config?.attendance_payroll?.monthly_working_days ?? 22),
    1,
  );
  const frequencyExecution = config?.frequency_execution ?? {};

  if (normalized === "fortnightly") {
    return Math.max(
      Number(
        frequencyExecution.fortnightly_month_equivalent_factor ?? 26 / 12,
      ),
      1,
    );
  }

  if (normalized === "daily") {
    return Math.max(
      Number(
        frequencyExecution.daily_month_equivalent_factor ?? attendanceDays,
      ),
      1,
    );
  }

  return Math.max(
    Number(frequencyExecution.weekly_month_equivalent_factor ?? 52 / 12),
    1,
  );
}

export function isSalaryBasisSupportedForFrequency(frequency, salaryBasis) {
  const payFrequency = normalizePayFrequency(frequency);
  const basis = normalizeSalaryBasis(salaryBasis);

  if (payFrequency === "monthly") return true;
  if (!["weekly", "fortnightly", "daily"].includes(payFrequency)) return false;

  return basis === "monthly" || basis === payFrequency;
}

export function buildFrequencyPreviewContext(employee, config = {}) {
  const frequency = normalizePayFrequency(employee?.pay_frequency);
  const salaryBasis = normalizeSalaryBasis(employee?.salary_basis);
  const monthlyEquivalentFactor = getMonthlyEquivalentFactor(frequency, config);
  const supported = isSalaryBasisSupportedForFrequency(frequency, salaryBasis);

  let runScale = 1;
  if (frequency !== "monthly" && supported) {
    runScale =
      salaryBasis === "monthly"
        ? Number((1 / monthlyEquivalentFactor).toFixed(10))
        : salaryBasis === frequency
          ? 1
          : 1;
  }

  return {
    frequency,
    salaryBasis,
    monthlyEquivalentFactor,
    runScale,
    supported,
    isMonthly: frequency === "monthly",
    previewMode:
      frequency === "monthly"
        ? "monthly_direct"
        : `${frequency}_monthly_equivalent`,
  };
}

export function roundMoney(value) {
  return Number(Number(value || 0).toFixed(2));
}

export function scaleStoredToCalculator(stored, runScale, monthlyEquivalentFactor) {
  return roundMoney(Number(stored || 0) * runScale * monthlyEquivalentFactor);
}

export function scaleStoredToPeriod(stored, runScale) {
  return roundMoney(Number(stored || 0) * runScale);
}

export function scaleStatutoryToPeriod(monthlyEquivalentAmount, monthlyEquivalentFactor) {
  const factor = Number(monthlyEquivalentFactor || 1);
  if (factor <= 0 || factor === 1) {
    return roundMoney(monthlyEquivalentAmount);
  }
  return roundMoney(Number(monthlyEquivalentAmount || 0) / factor);
}

export function getPayFrequencyLabel(frequency) {
  const normalized = normalizePayFrequency(frequency);
  if (normalized === "fortnightly") return "Fortnightly";
  return capitalizeToken(normalized);
}

export function getSalaryBasisLabel(salaryBasis) {
  return capitalizeToken(normalizeSalaryBasis(salaryBasis));
}

export function getPreviewPeriodLabel(frequency) {
  const normalized = normalizePayFrequency(frequency);
  if (normalized === "monthly") return "monthly pay period";
  if (normalized === "weekly") return "weekly pay period";
  if (normalized === "fortnightly") return "fortnightly pay period";
  return "daily pay period";
}

export function getBasicSalaryGuidance(context) {
  const frequency = normalizePayFrequency(context?.frequency);
  const salaryBasis = normalizeSalaryBasis(context?.salaryBasis);
  const supported = context?.supported !== false;

  if (frequency === "monthly") {
    return {
      primary: "Enter the employee's monthly basic salary.",
      secondary:
        "Allowances and statutory preview use the same monthly amounts.",
    };
  }

  if (!supported) {
    return {
      primary: "Enter basic salary using the employee's salary basis.",
      secondary:
        "Statutory preview may not match payroll until salary basis is compatible with this pay frequency on the employee profile.",
    };
  }

  if (salaryBasis === "monthly") {
    const periodWord =
      frequency === "daily"
        ? "day"
        : frequency === "fortnightly"
          ? "fortnight"
          : "week";
    return {
      primary: `Enter the monthly-equivalent basic salary (not the per-${periodWord} amount).`,
      secondary: `Payroll scales this to each ${getPreviewPeriodLabel(frequency)} when you run ${getPayFrequencyLabel(frequency).toLowerCase()} payroll.`,
    };
  }

  const periodLabel =
    frequency === "daily"
      ? "daily"
      : frequency === "weekly"
        ? "weekly"
        : "fortnightly";

  return {
    primary: `Enter the ${periodLabel} basic salary for each ${frequency === "daily" ? "day" : "pay period"}.`,
    secondary:
      "Statutory taxes are computed from a monthly equivalent, then scaled to this pay period in payroll.",
  };
}

export function getAllowancesGuidance(context) {
  const guidance = getBasicSalaryGuidance(context);
  return {
    primary: `Use the same basis as basic salary (${getSalaryBasisLabel(context?.salaryBasis)}).`,
    secondary: guidance.secondary,
  };
}

export function getUnsupportedSalaryBasisMessage(context) {
  const frequency = getPayFrequencyLabel(context?.frequency);
  const basis = getSalaryBasisLabel(context?.salaryBasis);

  return `${basis} salary basis is not supported for ${frequency.toLowerCase()} payroll runs. Use Monthly (monthly-equivalent amounts) or ${frequency} on the employee profile, or set pay frequency to Monthly.`;
}
